<template>
  <v-card flat>
    <div
      class="secondarytext--text text-h6 pa-4 cursor-pointer"
      @click="$emit('cancel')"
    >
      <v-icon class="mr-2" color="secondarytext">keyboard_arrow_left</v-icon>
      {{ title }}
    </div>
    <v-divider></v-divider>
    <v-card-text class="mt-4 pb-0">
      <v-form ref="form" v-model="valid">
        <v-row dense>
          <v-col cols="12" v-if="featureId == $enums.features.REVIEW">
            <v-select
              v-model="question.questionTypeId"
              :items="question_types"
              outlined
              item-text="name"
              item-value="id"
              label="Question Type"
              :rules="[$rules.required]"
              :color="getQuestionTypeColor(question.questionTypeId)"
              :prepend-inner-icon="getQuestionTypeIcon(question.questionTypeId)"
              :disabled="isEdit"
            >
            </v-select>
          </v-col>
          <v-col cols="12" v-else-if="featureId == $enums.features.PULSE">
            <v-text-field
              outlined
              readonly
              value="0 - 10 scale (0 = Strongly disagree & 10 = Strongly agree)"
              append-icon="lock"
              :color="getQuestionTypeColor($enums.questionTypes.OPINION_SCALE)"
              label="Question Type"
            >
              <template v-slot:prepend-inner>
                <v-icon
                  :color="
                    getQuestionTypeColor($enums.questionTypes.OPINION_SCALE)
                  "
                >
                  {{ getQuestionTypeIcon($enums.questionTypes.OPINION_SCALE) }}
                </v-icon>
              </template>
              <template v-slot:append>
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }"
                    ><v-icon v-bind="attrs" v-on="on">
                      lock
                    </v-icon>
                  </template>
                  <div class="tooltip-limit-width">
                    Culture tracking currently supports this one question type
                  </div>
                </v-tooltip>
              </template>
            </v-text-field>
          </v-col>
          <v-col cols="12">
            <v-textarea
              outlined
              v-model="question.question"
              label="Question"
              :placeholder="placeholder"
              :rules="[$rules.required]"
              :disabled="isEdit"
              maxlength="150"
            >
            </v-textarea>
          </v-col>
          <v-col cols="12" md="6">
            <v-select
              v-model="question.categoryId"
              :items="categories"
              outlined
              item-text="name"
              item-value="id"
              label="Question Category"
            >
            </v-select>
          </v-col>
          <v-col cols="12" md="6" v-if="featureId == $enums.features.REVIEW">
            <v-select
              v-model="alignment"
              :items="coreValueItems"
              outlined
              item-text="name"
              item-value="id"
              label="Culture Alignment"
            >
            </v-select>
          </v-col>
          <template v-if="featureId == $enums.features.PULSE">
            <v-col cols="12" md="6">
              <v-select
                v-model="question.subCategory.id"
                :items="sub_categories"
                :rules="[$rules.requiredAllowEmpty]"
                outlined
                item-text="name"
                item-value="id"
                label="Question Sub-Category"
              >
                <template v-slot:selection="{ item }">
                  <v-icon v-if="item.icon" class="mr-2" color="secondarytext">
                    {{ item.icon }}
                  </v-icon>
                  {{ item.name }}
                </template>
                <template v-slot:item="{ item, attrs, on }">
                  <v-list-item v-on="on" v-bind="attrs">
                    <v-list-item-icon>
                      <v-icon v-if="item.icon">{{ item.icon }}</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ item.name }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-select>
            </v-col>
            <template v-if="question.subCategory.id == ''">
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="question.subCategory.name"
                  outlined
                  label="New Sub-Category Name"
                  :rules="[$rules.required]"
                  maxlength="100"
                >
                </v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-textarea
                  outlined
                  v-model="question.subCategory.description"
                  label="New Sub-Category Description"
                  maxlength="500"
                  :rules="[$rules.required]"
                >
                </v-textarea>
              </v-col>
            </template>
          </template>
        </v-row>
      </v-form>
    </v-card-text>
    <v-card-actions>
      <v-btn color="secondary" @click="save" :loading="saving">
        {{ saveText }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapGetters } from "vuex";
import questionTypes from "@/helpers/mixins/questionTypes";
export default {
  name: "EditQuestionForm",
  mixins: [questionTypes],
  props: {
    question: { type: Object },
    featureId: { type: Number }
  },
  data: () => ({
    saving: false,
    valid: false,
    showSubCategoryAdd: false,
    questionTypes: questionTypes
  }),
  computed: {
    isEdit() {
      return !this._.isEmpty(this.question.id);
    },
    title() {
      return this.isEdit ? "Edit question" : "Create new question";
    },
    saveText() {
      return this.isEdit ? "Update Question" : "Add Question";
    },
    alignment: {
      get() {
        if (this.question.isMission) {
          return "mission";
        }
        return this.question.relatedId || null;
      },
      set(val) {
        this.question.isMission = val == "mission";
        this.question.relatedId = val == "mission" ? null : val;
      }
    },
    placeholder() {
      return this.question.questionTypeId ==
        this.$enums.questionTypes.OPINION_SCALE ||
        this.featureId == this.$enums.features.PULSE
        ? "Scale questions should be phrased as a statement"
        : "Write in your question";
    },
    question_types() {
      return this.survey_question_types.filter(
        x => x.id != this.$enums.questionTypes.MULTIPLE_CHOICE
      );
    },
    coreValueItems() {
      let list = this.core_values.map(x => ({
        id: x.id,
        name: x.name
      }));
      list.unshift({ id: "mission", name: this.t("mission.title") });
      list.unshift({ id: null, name: "None" });
      return list;
    },
    sub_categories() {
      let category = this.categories.find(
        x => x.id == this.question.categoryId
      );
      if (category) {
        let items = category.subCategories;
        return items.concat({
          id: "",
          name: "Add Sub-Category",
          icon: "add_circle"
        });
      }
      return null;
    },
    categories() {
      return this.$store.getters[this.categoriesGetter];
    },
    categoriesGetter() {
      switch (this.featureId) {
        case this.$enums.features.PULSE:
          return "pulse/categories";
        case this.$enums.features.REVIEW:
          return "settings/review_categories";
      }
      return "";
    },
    saveAction() {
      switch (this.featureId) {
        case this.$enums.features.PULSE:
          return "pulse/saveQuestion";
        case this.$enums.features.REVIEW:
          return "settings/saveReviewQuestion";
      }
      return "";
    },
    ...mapGetters({
      core_values: "company/coreValues",
      survey_question_types: "lookup/survey_question_types"
    })
  },
  methods: {
    save() {
      if (this.$refs.form.validate()) {
        this.saving = true;
        this.$store
          .dispatch(this.saveAction, this.question)
          .then(id => {
            this.$emit("save", id);
            this.$root.$snackbarSuccess(
              this.isEdit ? "Question saved" : "Question added"
            );
          })
          .catch(error => {
            this.logError(error);
            this.$root.$alert(
              "Error Occurred",
              "An error occurred while saving your question."
            );
          })
          .finally(() => {
            this.saving = false;
          });
      }
    }
  }
};
</script>
